import { zodResolver } from "@hookform/resolvers/zod";
import {
  Delete,
  Edit,
  PlusOne,
  SearchOff
} from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from "@mui/material";
import React, { useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { getManagementUnitTypes } from "../../api/managementUnits";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";
import { useAccountPermissions } from "../../hooks/Permissions/useAccountPermissions";
import { useEntityAccessPermissions } from "../../hooks/Permissions/useEntityAccessPermissions";
import {
  ManagementUnitForm,
  getManagementUnitFormSchema,
} from "../../types/forms/managementUnit";
import { CreateManagementUnitRequest, ManagementUnit, ManagementUnitResponse, ManagementUnitType, UpdateManagementUnitRequest } from "../../types/model/managementUnit";

interface ManagementUnitsTableProps {
  entityId: number;
  entityName: string,
  tableData: ManagementUnitResponse[];
  onCreate: (values: CreateManagementUnitRequest) => Promise<void>;
  onUpdate: (id: number, values: UpdateManagementUnitRequest) => Promise<void>;
  onDelete: (managementUnitId: number) => Promise<void>;
}

const ManagementUnitsTable = ({
  entityId,
  entityName,
  tableData,
  onCreate,
  onUpdate,
  onDelete
}: ManagementUnitsTableProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  
  const { canEditData } = useAccountPermissions();
  const {
    canEditEntity,
    isLoading: isLoadingEntityPermissions,
  } = useEntityAccessPermissions();

  const canEdit =
    canEditData &&
    !isLoadingEntityPermissions &&
    canEditEntity(entityId);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);

  const [openDialog, setOpenDialog] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<ManagementUnit | null>(null);


  const [isSaving, setIsSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const [managementUnitTypes, setManagementUnitTypes] = React.useState<ManagementUnitType[]>([]);

  const validationSchema = getManagementUnitFormSchema(t);
  const methods = useForm<ManagementUnitForm>({
    resolver: zodResolver(validationSchema),
  });

  useEffect(() => {
    const fetchManagementUnitTypes = async () => {
      try {
        const types = await getManagementUnitTypes();
        setManagementUnitTypes(types);
      } catch (error) {
        handleError(error);
      }
    };

    fetchManagementUnitTypes();
  }, []);

  useEffect(() => {
    if (openDialog) {
      methods.reset({
        name: editingRow?.name || "",
        notes: editingRow?.notes || "",
        unitTypeId: editingRow?.unitTypeId ?? undefined,
      });
    }
  }, [openDialog, editingRow, methods]);

  const openCreateDialog = () => {
    setEditingRow(null);
    setOpenDialog(true);
  };

  const openUpdateDialog = (row: ManagementUnit) => {
    setEditingRow(row);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    if (isSaving) return;
    setOpenDialog(false);
  };

  const handleSave = async (values: ManagementUnitForm) => {
    setIsSaving(true);
    try {
      if (editingRow) {
        await onUpdate(editingRow.id, values);
      } else {
        await onCreate(values);
      }
      handleCloseDialog();
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await onDelete(id);
    } catch (error) {
      handleError(error);
    } finally {
      setDeletingId(null);
    }
  }

  const handleClick = (id: number) => {
    navigate(`/management-units/${id}`);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(Number.parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleRows = tableData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2, p: 2 }}>
        <Toolbar sx={{ pl: 2, pr: 1 }}>
          <Typography sx={{ flex: "1 1 100%" }} variant="h5" component="div">
            {t("entities.entity.management-units.title", {
              entityName: entityName?.toUpperCase(),
            })}
          </Typography>

          {/* Add management unit button */}
          {canEdit && (
            <Tooltip title={t("entities.entity.management-units.add-new")}>
              <IconButton onClick={openCreateDialog}>
                <PlusOne />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>

        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t("entities.entity.management-units.name")}</TableCell>
                <TableCell>{t("entities.entity.management-units.type")}</TableCell>
                {canEdit && (<TableCell align="right">{t("generic.actions")}</TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={canEdit ? 3 : 2} align="center" sx={{ py: 10 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        opacity: 0.6,
                      }}
                    >
                      <SearchOff sx={{ fontSize: 64, mb: 2 }} />
                      <Typography variant="h6">
                        {t("entities.entity.management-units.no-management-units")}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                visibleRows.map((row) => {
                  const isDeleting = deletingId === row.id;
                  return (
                    <TableRow
                      hover
                      key={row.id}
                      onClick={() => handleClick(row.id)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        {managementUnitTypes.some((type) => type.id === row.unitTypeId)
                          ? t(
                              managementUnitTypes.find(
                                (type) => type.id === row.unitTypeId
                              )!.i18nLabel
                            )
                          : "—"}
                      </TableCell>
                      {canEdit && (
                        <TableCell align="right">
                          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                            <Tooltip title={t("generic.edit")}>
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openUpdateDialog(row);
                                  }}
                                  disabled={isDeleting}
                                >
                                  <Edit />
                                </IconButton>
                              </span>
                            </Tooltip>

                            <Tooltip title={t("generic.delete")}>
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(row.id);
                                  }}
                                  disabled={isDeleting}
                                >
                                  {isDeleting ? (
                                    <CircularProgress size={24} />
                                  ) : (
                                    <Delete />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[25, 50, 100]}
          component="div"
          count={tableData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* Add/Update Dialog */}        
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
        slotProps={{
          transition: {
            onExited: () => setEditingRow(null),
          },
        }}
      >
        <FormProvider {...methods}>
          <Box component="form" onSubmit={methods.handleSubmit(handleSave)}>
            <DialogTitle>
              {editingRow
                ? t("entities.entity.management-units.edit")
                : t("entities.entity.management-units.add-new")
              }
            </DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection:"column", mt: 1 }}>
              <TextField
                fullWidth
                label={`${t("management-units.management-unit.general.name")} *`}
                {...methods.register("name")}
                error={!!methods.formState.errors.name}
                helperText={methods.formState.errors.name?.message}
                margin="normal"
                disabled={isSaving}
              />
              <Controller
                name="unitTypeId"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label={`${t("management-units.management-unit.general.type")} *`}
                    variant="outlined"
                    disabled={isSaving}
                    value={
                      managementUnitTypes.some((o) => o.id === field.value)
                        ? field.value
                        : ""
                    }
                    onChange={(e) =>
                      field.onChange(e.target.value ? Number(e.target.value) : null)
                    }
                    error={!!methods.formState.errors.unitTypeId}
                    helperText={methods.formState.errors.unitTypeId?.message}
                    margin="normal"
                  >
                    <MenuItem value="">
                      —
                    </MenuItem>

                    {managementUnitTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {t(type.i18nLabel)}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
              <TextField
                fullWidth
                label={t("entities.entity.management-units.notes")}
                sx={{ mt: 2 }}
                {...methods.register("notes")}
                multiline
                rows={4}
                disabled={isSaving}
              />
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                color="secondary"
                onClick={handleCloseDialog}
                disabled={isSaving}
              >
                {t("generic.cancel")}
              </Button>
              <Button type="submit" variant="contained" disabled={isSaving}>
                {isSaving ? <CircularProgress size={24} /> : t("generic.save")}
              </Button>
            </DialogActions>
          </Box>
        </FormProvider>
      </Dialog>
    </Box>
  );
};

export default ManagementUnitsTable;